import type {
  EstadoTienda,
  EventoHistorial,
  FilaVista,
  MetaTiendaVista,
  MiAlcance,
  OpcionMotivo,
  ParametrosMetas,
  RevisionAutomatica,
  TiendaSemana,
} from '../types/metas.types';
import { ETIQUETA_MOTIVO } from '../types/metas.types';
import { esMetaABodeguero, TEXTO_META_A_BODEGUERO } from './bodeguero';
import { esFilaCasa, TEXTO_META_A_CASA } from './casa';
import { veTodasLasTiendas } from './direccion';
import { momentoMx, nombrePropio, pctConSigno, pesosEnteros } from './formato';
import { textoDesglosePuesto, textoMetaIncompleta } from './kpis';
import { esPendiente } from './totales';

/**
 * Reglas puras de la pestaña Autorización (fase 3 del rediseño, artboards
 * `Autorizacion` y `MovilAutorizacion`). Sin React: filtros, conteos, textos de
 * la lista, de la revisión automática y del historial.
 */

// ------------------------------------------------------------ filtros

/**
 * Los chips del diseño. Desde el 21-oct-2026 (fase B, diseño `Regional-filtro`)
 * cada tienda dice con quién está: «Con el supervisor» (`revision`, se la mandó
 * el gerente) y «Con el gerente» (sin captura y borrador: todavía no la manda,
 * o se la devolvieron). Antes, «En captura» juntaba sin captura y borrador.
 *
 * 22-oct-2026 (decisión del usuario: «de momento seguir dejando la tarea de
 * capturar la meta a los supervisores»): con la captura de la tienda apagada
 * (`capturaTienda`, por omisión) vuelven los chips de antes, con «En captura»,
 * que junta además lo que quedara «con el supervisor» de cuando estaba encendida.
 */
export type FiltroAutorizacion =
  | 'por_autorizar'
  | 'con_supervisor'
  | 'con_gerente'
  | 'rechazadas'
  | 'autorizadas'
  | 'en_captura'
  | 'todas';

/** Con la captura de la tienda encendida (fase B). */
export const FILTROS_AUTORIZACION: { id: FiltroAutorizacion; etiqueta: string }[] = [
  { id: 'por_autorizar', etiqueta: 'Por autorizar' },
  { id: 'con_supervisor', etiqueta: 'Con el supervisor' },
  { id: 'con_gerente', etiqueta: 'Con el gerente' },
  { id: 'rechazadas', etiqueta: 'Rechazadas' },
  { id: 'autorizadas', etiqueta: 'Autorizadas' },
  { id: 'todas', etiqueta: 'Todas' },
];

/** El flujo de dos pasos, el de antes (22-oct-2026): sin el gerente. */
export const FILTROS_AUTORIZACION_SUPERVISOR: { id: FiltroAutorizacion; etiqueta: string }[] = [
  { id: 'por_autorizar', etiqueta: 'Por autorizar' },
  { id: 'rechazadas', etiqueta: 'Rechazadas' },
  { id: 'autorizadas', etiqueta: 'Autorizadas' },
  { id: 'en_captura', etiqueta: 'En captura' },
  { id: 'todas', etiqueta: 'Todas' },
];

/** Los chips según el interruptor del entorno (`capturaDeTienda`); por omisión, sin la tienda. */
export const filtrosAutorizacion = (capturaTienda = false): { id: FiltroAutorizacion; etiqueta: string }[] =>
  capturaTienda ? FILTROS_AUTORIZACION : FILTROS_AUTORIZACION_SUPERVISOR;

/** Todos los filtros que existen, de los dos flujos (para contar y nombrar). */
const TODOS_LOS_FILTROS: { id: FiltroAutorizacion; etiqueta: string }[] = [
  ...FILTROS_AUTORIZACION,
  ...FILTROS_AUTORIZACION_SUPERVISOR.filter((f) => !FILTROS_AUTORIZACION.some((x) => x.id === f.id)),
];

/**
 * El nombre del chip. En «Seguimiento» (`soloLectura`, dirección) las enviadas
 * no son «por autorizar» para quien mira: «Con el regional», igual que su
 * pastilla en la lista (`textoEstadoLista`; revisión de la fase B, 21-oct-2026).
 */
export const etiquetaFiltro = (f: FiltroAutorizacion, soloLectura = false): string =>
  soloLectura && f === 'por_autorizar' ? 'Con el regional' : TODOS_LOS_FILTROS.find((x) => x.id === f)?.etiqueta ?? 'Todas';

export function coincideFiltro(estado: EstadoTienda, f: FiltroAutorizacion): boolean {
  switch (f) {
    case 'por_autorizar':
      return estado === 'enviada';
    case 'con_supervisor':
      return estado === 'revision';
    case 'con_gerente':
      return estado === 'sin_captura' || estado === 'borrador';
    case 'en_captura':
      return estado === 'sin_captura' || estado === 'borrador' || estado === 'revision';
    case 'rechazadas':
      return estado === 'rechazada';
    case 'autorizadas':
      return estado === 'autorizada';
    default:
      return true;
  }
}

export function conteosPorFiltro(tiendas: { estado: EstadoTienda }[]): Record<FiltroAutorizacion, number> {
  const c: Record<FiltroAutorizacion, number> = {
    por_autorizar: 0,
    con_supervisor: 0,
    con_gerente: 0,
    rechazadas: 0,
    autorizadas: 0,
    en_captura: 0,
    todas: 0,
  };
  for (const t of tiendas) {
    c.todas++;
    for (const f of TODOS_LOS_FILTROS) if (f.id !== 'todas' && coincideFiltro(t.estado, f.id)) c[f.id]++;
  }
  return c;
}

/** Por omisión «Por autorizar» si hay alguna; si no, «Todas» (una lista vacía no dice nada). */
export const filtroPorOmision = (c: Record<FiltroAutorizacion, number>): FiltroAutorizacion =>
  c.por_autorizar > 0 ? 'por_autorizar' : 'todas';

/** Las marcas diacríticas combinables (U+0300–U+036F) que deja `normalize('NFD')`. */
const DIACRITICOS = new RegExp(`[${String.fromCharCode(0x300)}-${String.fromCharCode(0x36f)}]`, 'g');

/** Sin acentos ni mayúsculas: «satelite» encuentra «SATÉLITE». */
export const normalizar = (s: string): string =>
  s
    .normalize('NFD')
    .replace(DIACRITICOS, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();

/**
 * «Tienda o supervisor»: busca en cadena y número («vaz 3», «vaz3»), el nombre
 * de la tienda y el supervisor. Todas las palabras tienen que aparecer.
 */
export function coincideBusqueda(
  t: { chain: string; num: number; tienda: string; supervisor: string },
  q: string,
): boolean {
  const palabras = normalizar(q).split(' ').filter(Boolean);
  if (!palabras.length) return true;
  const texto = normalizar(`${t.chain} ${t.num} ${t.chain}${t.num} ${t.tienda} ${t.supervisor}`);
  return palabras.every((p) => texto.includes(p));
}

export interface OpcionRegional {
  nombre: string;
  tiendas: number;
}

/** Cuántas tiendas tiene cada nombre, en orden alfabético. */
function contarPorNombre(nombres: string[]): OpcionRegional[] {
  const m = new Map<string, number>();
  for (const n of nombres) m.set(n, (m.get(n) ?? 0) + 1);
  return Array.from(m.entries())
    .map(([nombre, n]) => ({ nombre, tiendas: n }))
    .sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'));
}

/** Los regionales de la lista, con cuántas tiendas tiene cada uno («Arturo Barrios · 12 tiendas»). */
export function regionalesDe(tiendas: { regional: string }[]): OpcionRegional[] {
  return contarPorNombre(tiendas.map((t) => t.regional || '(sin regional)'));
}

/** El supervisor de una tienda como lo compara el filtro (una sin supervisor también se puede elegir). */
export const supervisorDe = (t: { supervisor: string }): string => t.supervisor || '(sin supervisor)';

/**
 * Los supervisores de la lista, con cuántas tiendas tiene cada uno («Esther
 * Morales · 11 tiendas»). Quien llama pasa ya las del regional elegido: el
 * filtro de supervisor depende del de regional.
 */
export function supervisoresDe(tiendas: { supervisor: string }[]): OpcionRegional[] {
  return contarPorNombre(tiendas.map(supervisorDe));
}

/**
 * Filtro por supervisor (21-oct-2026, pedido para el visor del regional): lo
 * ven quienes miran tiendas de más de un supervisor —el regional, el
 * administrador y dirección—. El supervisor sólo ve las suyas: no le sirve.
 */
export const eligeSupervisor = (
  a: Pick<MiAlcance, 'esAdmin' | 'alcances'> & Partial<Pick<MiAlcance, 'esDireccion'>>,
): boolean => veTodasLasTiendas(a) || a.alcances.some((x) => x.tipo === 'regional');

export const textoTiendas = (n: number): string => (n === 1 ? '1 tienda' : `${n} tiendas`);

// ------------------------------------------------------------ lista

export const llaveFila = (t: { chain: string; num: number }): string => `${t.chain}|${t.num}`;

export const nombreTienda = (t: { chain: string; num: number; tienda: string }): string =>
  `${t.chain} ${t.num} · ${t.tienda}`;

/**
 * La pastilla de la lista dice CON QUIÉN está la tienda (21-oct-2026, diseños
 * `Regional-filtro` y `Direccion-seguimiento`): sin captura y borrador, «Con
 * el gerente»; `revision`, «Con el supervisor»; una enviada, «Por autorizar»
 * para quien la autoriza y «Con el regional» en «Seguimiento» (`soloLectura`).
 * `undefined` = el nombre de siempre (Autorizada, Rechazada).
 *
 * Con la captura de la tienda apagada (`capturaTienda`, por omisión; 22-oct-2026,
 * decisión del usuario) no hay gerente: sin captura, borrador y lo que quedara
 * «con el supervisor» se leen «En captura», como antes. La enviada, igual.
 */
export function textoEstadoLista(e: EstadoTienda, soloLectura = false, capturaTienda = false): string | undefined {
  switch (e) {
    case 'sin_captura':
    case 'borrador':
      return capturaTienda ? 'Con el gerente' : 'En captura';
    case 'revision':
      return capturaTienda ? 'Con el supervisor' : 'En captura';
    case 'enviada':
      return soloLectura ? 'Con el regional' : 'Por autorizar';
    default:
      return undefined;
  }
}

/** «Hoy 09:14», «Ayer 18:20» o «26 sep, 14:05». */
export function momentoCapital(v: string | null | undefined, ahora?: number): string {
  const m = momentoMx(v, ahora);
  return m ? m.charAt(0).toLocaleUpperCase('es-MX') + m.slice(1) : '';
}

/** «el gerente (Carmen Vega)» o «el gerente», para decir quién capturó en la tienda. */
export const gerenteDe = (nombre: string | null | undefined): string => {
  // La persona de «Cambiar usuario» viene de BUK en MAYÚSCULAS; un nombre ya escrito se respeta.
  const n = nombrePropioSiMayusculas((nombre ?? '').trim());
  return n ? `el gerente (${n})` : 'el gerente';
};

/**
 * El renglón bajo el nombre de la tienda, como en el diseño:
 *   «Karla Ríos · enviada hoy 09:14» · «Diana Flores · rechazada hoy 10:02»
 *   «Autorizada ayer 16:40» · «Esther Morales · borrador hoy 10:42»
 * Con el flujo del gerente (21-oct-2026): «Esther Morales · se la envió el
 * gerente (Carmen Vega) hoy 18:40», «… · devuelta al gerente hoy 10:02» y, en
 * una que pasó por él, «… · enviada hoy 09:14 · capturó el gerente».
 */
export function subtituloTienda(t: TiendaSemana, ahora?: number): string {
  const sup = nombrePropio(t.supervisor);
  const con = (texto: string) => [sup, texto].filter(Boolean).join(' · ');
  const capturo = t.enviadoGerenteEn ? ` · capturó ${gerenteDe(t.enviadoGerenteNombre)}` : '';
  switch (t.estado) {
    case 'revision':
      return con(`se la envió ${gerenteDe(t.enviadoGerenteNombre)} ${momentoMx(t.enviadoGerenteEn, ahora)}`.trim());
    case 'enviada':
      return con(`enviada ${momentoMx(t.enviadoEn, ahora)}`.trim()) + capturo;
    case 'rechazada':
      return con(`rechazada ${momentoMx(t.resueltoEn, ahora)}`.trim());
    case 'autorizada':
      return `Autorizada ${momentoMx(t.resueltoEn, ahora)}`.trim() + capturo;
    case 'borrador':
      return t.devuelta
        ? con(`devuelta al gerente ${momentoMx(t.devueltaEn, ahora)}`.trim())
        : con(`borrador ${momentoMx(t.actualizadoEn, ahora)}`.trim());
    default:
      return con('sin captura');
  }
}

/** Tono del «Dif.»: rojo si falta, verde si sobra, neutro si redondea a 0.0 %. */
export function tonoDiferencia(pct: number | null | undefined): 'baja' | 'alta' | 'neutra' {
  const s = pctConSigno(pct);
  if (s.startsWith('−')) return 'baja';
  if (s.startsWith('+')) return 'alta';
  return 'neutra';
}

/** «2º envío». */
export const ordinalEnvio = (n: number): string => `${n}º envío`;

/**
 * Quién, cuándo y cuántas veces, bajo el nombre de la tienda en el detalle:
 * «2º envío, hoy 09:14», «enviada hoy 09:14», «autorizada ayer 16:40»…
 */
export function momentoDeEstado(
  e: {
    estado: EstadoTienda;
    numEnvio?: number | null;
    enviadoEn: string | null;
    resueltoEn: string | null;
    actualizadoEn: string | null;
    /** Cuándo la mandó el gerente al supervisor (21-oct-2026). */
    enviadoGerenteEn?: string | null;
  },
  ahora?: number,
): string {
  switch (e.estado) {
    case 'revision': {
      const m = momentoMx(e.enviadoGerenteEn, ahora);
      return m ? `con el supervisor desde ${m}` : 'con el supervisor';
    }
    case 'enviada': {
      const n = Number(e.numEnvio ?? 0);
      const m = momentoMx(e.enviadoEn, ahora);
      return n > 1 ? `${ordinalEnvio(n)}${m ? `, ${m}` : ''}` : `enviada ${m}`.trim();
    }
    case 'autorizada':
      return `autorizada ${momentoMx(e.resueltoEn, ahora)}`.trim();
    case 'rechazada':
      return `rechazada ${momentoMx(e.resueltoEn, ahora)}`.trim();
    case 'borrador':
      return `borrador ${momentoMx(e.actualizadoEn, ahora)}`.trim();
    default:
      return 'sin captura';
  }
}

/**
 * «Seguimiento» de dirección (21-oct-2026): QUIÉN envió y quién resolvió, con
 * cuándo — «Envió Esther Morales · hoy 09:14», «Autorizó Arturo Barrios · ayer
 * 16:40» o «Rechazó …». El nombre que da la API (`…PorNombre`: el de la cuenta
 * o, si falta, su correo). Si la capturó el gerente (fase B), antes «Capturó el
 * gerente (Carmen Vega) · mié 18:40». Nada mientras la tienda siga en captura:
 * todavía no hay quién.
 */
export function quienEnvioYResolvio(
  t: Pick<TiendaSemana, 'estado' | 'enviadoPor' | 'enviadoPorNombre' | 'enviadoEn' | 'resueltoPor' | 'resueltoPorNombre' | 'resueltoEn'> &
    Partial<Pick<TiendaSemana, 'enviadoGerenteEn' | 'enviadoGerenteNombre'>>,
  ahora?: number,
): string[] {
  const linea = (verbo: string, quien: string | null | undefined, cuando: string | null | undefined) =>
    [`${verbo} ${quien || '—'}`, momentoMx(cuando, ahora)].filter(Boolean).join(' · ');
  const out: string[] = [];
  if (t.enviadoGerenteEn && t.estado !== 'borrador' && t.estado !== 'sin_captura')
    out.push(linea('Capturó', gerenteDe(t.enviadoGerenteNombre), t.enviadoGerenteEn));
  if (t.estado !== 'enviada' && t.estado !== 'autorizada' && t.estado !== 'rechazada') return out;
  out.push(linea('Envió', t.enviadoPorNombre || t.enviadoPor, t.enviadoEn));
  if (t.estado === 'autorizada') out.push(linea('Autorizó', t.resueltoPorNombre || t.resueltoPor, t.resueltoEn));
  if (t.estado === 'rechazada') out.push(linea('Rechazó', t.resueltoPorNombre || t.resueltoPor, t.resueltoEn));
  return out;
}

/**
 * El avance de la semana para «Seguimiento» (diseño `Direccion-seguimiento`,
 * 21-oct-2026): cuántas autorizadas, cuántas con el regional (enviadas), con
 * el supervisor y con el gerente o sin captura. Una rechazada la corrige el
 * supervisor: cuenta con él.
 */
export function avanceSemana(tiendas: { estado: EstadoTienda }[]): {
  autorizadas: number;
  conRegional: number;
  conSupervisor: number;
  conGerente: number;
  total: number;
} {
  const a = { autorizadas: 0, conRegional: 0, conSupervisor: 0, conGerente: 0, total: tiendas.length };
  for (const t of tiendas) {
    if (t.estado === 'autorizada') a.autorizadas++;
    else if (t.estado === 'enviada') a.conRegional++;
    else if (t.estado === 'revision' || t.estado === 'rechazada') a.conSupervisor++;
    else a.conGerente++;
  }
  return a;
}

/**
 * Las cuatro cifras del avance de «Seguimiento», con su nombre. Con la captura
 * de la tienda encendida, las de la fase B (`avanceSemana`). Apagada (por
 * omisión; 22-oct-2026, decisión del usuario) es el flujo de dos pasos: lo
 * rechazado va aparte y todo lo que no se ha enviado está «En captura» (con el
 * supervisor; también lo que quedara «con el supervisor» de antes).
 */
export function kpisAvance(
  tiendas: { estado: EstadoTienda }[],
  capturaTienda = false,
): { etiqueta: string; valor: number; de?: number }[] {
  const a = avanceSemana(tiendas);
  const inicio = [
    { etiqueta: 'Autorizadas', valor: a.autorizadas, de: a.total },
    { etiqueta: 'Con el regional', valor: a.conRegional },
  ];
  if (capturaTienda)
    return [...inicio, { etiqueta: 'Con el supervisor', valor: a.conSupervisor }, { etiqueta: 'Con el gerente o sin captura', valor: a.conGerente }];
  const rechazadas = tiendas.filter((t) => t.estado === 'rechazada').length;
  return [...inicio, { etiqueta: 'Rechazadas', valor: rechazadas }, { etiqueta: 'En captura', valor: a.total - a.autorizadas - a.conRegional - rechazadas }];
}

export const TAMANO_PAGINA = 8;

export function paginar<T>(lista: T[], pagina: number, tamano = TAMANO_PAGINA): { filas: T[]; pagina: number; paginas: number } {
  const paginas = Math.max(1, Math.ceil(lista.length / tamano));
  const p = Math.min(Math.max(0, pagina), paginas - 1);
  return { filas: lista.slice(p * tamano, p * tamano + tamano), pagina: p, paginas };
}

/**
 * La página de `lista` donde está la de llave `llave` (`null` si no está). Al
 * abrir una tienda desde el enlace o desde otra pestaña, la lista va a su
 * página: si no, el detalle de VAZ 56 quedaba abierto con la lista en la
 * página 1 y no se veía cuál era la elegida (revisión de la fase B, 21-oct-2026).
 */
export function paginaDe<T>(lista: T[], llave: string | null, llaveDe: (t: T) => string, tamano = TAMANO_PAGINA): number | null {
  if (!llave) return null;
  const i = lista.findIndex((t) => llaveDe(t) === llave);
  return i < 0 ? null : Math.floor(i / tamano);
}

// ------------------------------------------------------------ revisión automática

export type TonoChequeo = 'ok' | 'alerta' | 'info';

export interface Chequeo {
  id: 'cobertura' | 'bajo-promedio' | 'muy-alta' | 'suma' | 'bodeguero' | 'casa';
  tono: TonoChequeo;
  titulo: string;
  /** Renglones pequeños bajo el título (uno por persona en «bajo el promedio»). */
  detalle: string[];
}

/** La etiqueta de un motivo: la de `mi-alcance` si la trae; si no, la de respaldo. */
export function etiquetaDeMotivo(valor: string, motivos: OpcionMotivo[] = []): string {
  return motivos.find((m) => m.valor === valor)?.etiqueta ?? ETIQUETA_MOTIVO[valor as keyof typeof ETIQUETA_MOTIVO] ?? valor;
}

/** «Iván Castro Medina» → «Iván Castro» (el teléfono no tiene espacio para los dos apellidos). */
export function nombreCorto(nombre: string): string {
  const p = nombrePropio(nombre).split(' ').filter(Boolean);
  return p.slice(0, 2).join(' ');
}

/**
 * Metas de MÁS de `factorMax` veces la venta promedio (un cero de más). La API
 * las cuenta en `alertas` pero no las lista en la revisión; aquí salen de las
 * filas de la vista, para que el conteo de alertas y los chequeos cuadren.
 *
 * Sin el bodeguero con meta ni la Casa (23-oct-2026, ronda F, decisión de la
 * sesión, revertible): el promedio de bodega es bajo por naturaleza y «meta
 * mayor al doble» sacaba a la tienda del lote por algo que su propio aviso ya
 * dice (TAF 188, S43). La Casa no tiene promedio de persona. La API hace lo
 * mismo (`revisionAutomatica`), así que el conteo sigue cuadrando.
 */
export function metasMuyAltas(filas: FilaVista[], factorMax: number): FilaVista[] {
  if (!(factorMax > 0)) return [];
  return filas.filter((f) => {
    if (esMetaABodeguero(f) || esFilaCasa(f)) return false;
    const meta = Number(f.metaMonto);
    const prom = f.ventaPromedio ?? null;
    return f.metaMonto !== null && Number.isFinite(meta) && meta > 0 && prom !== null && prom > 0 && meta > factorMax * prom;
  });
}

/**
 * Los chequeos del panel, en palabras. Nunca sólo color: cada uno lleva su
 * ícono (según `tono`) y su texto. `corto` es la versión del teléfono.
 */
/**
 * «Colaboradores listos» con la definición ÚNICA (21-oct-2026), sobre las filas
 * de la vista: de las que su puesto OBLIGA a llevar meta, cuántas tienen meta o
 * motivo, con los motivos de ésas. Las opcionales (gerente, subgerente) y las
 * que no llevan meta por su puesto van aparte. La `cobertura` de la API cuenta
 * a todas («10 de 10» en VAZ 56, donde la captura decía «4 de 4»).
 */
export function coberturaDeFilas(filas: FilaVista[]): RevisionAutomatica['cobertura'] & { sinMetaPorPuesto: number; opcionales: number } {
  let total = 0;
  let pendientes = 0;
  let sinMetaPorPuesto = 0;
  let opcionales = 0;
  const porMotivo: Record<string, number> = {};
  for (const f of filas) {
    // La Casa (ronda F) no es una persona: no cuenta en ningún lado; tiene su chequeo.
    if (esFilaCasa(f)) continue;
    const regla = f.reglaMeta ?? 'obligatoria';
    if (regla === 'opcional') opcionales++;
    // Un bodeguero CON meta (23-oct-2026) no es «sin meta por su puesto»; va en su chequeo.
    else if (regla === 'no_lleva') {
      if (!esMetaABodeguero(f)) sinMetaPorPuesto++;
    } else {
      total++;
      if (esPendiente(f)) pendientes++;
      else if ((f.metaMonto === null || f.metaMonto === undefined) && f.sinMetaMotivo)
        porMotivo[f.sinMetaMotivo] = (porMotivo[f.sinMetaMotivo] ?? 0) + 1;
    }
  }
  return { listos: total - pendientes, total, porMotivo, sinMetaPorPuesto, opcionales };
}

export function chequeosRevision(
  r: RevisionAutomatica,
  opciones: {
    motivos?: OpcionMotivo[];
    parametros?: Pick<ParametrosMetas, 'umbralBajoPromedio' | 'toleranciaSuma' | 'factorMax'>;
    muyAltas?: FilaVista[];
    corto?: boolean;
    /**
     * La meta de tienda de la vista: sin suma que comparar, dice si es que está
     * incompleta («6 de 7 días») o llegó con IVA, como el recuadro de la Captura.
     */
    metaTienda?: MetaTiendaVista | null;
  } = {},
): Chequeo[] {
  const { motivos = [], parametros, muyAltas = [], corto = false, metaTienda } = opciones;
  const out: Chequeo[] = [];

  // 1. Cobertura: todos con meta o motivo.
  const { listos, total, porMotivo } = r.cobertura;
  const motivosTexto = Object.entries(porMotivo)
    .filter(([, n]) => n > 0)
    .map(([m, n]) => (corto ? etiquetaDeMotivo(m, motivos) : `${etiquetaDeMotivo(m, motivos)} (${n})`));
  const sinMeta = Object.values(porMotivo).reduce((a, n) => a + (n > 0 ? n : 0), 0);
  const detalleCob: string[] = [];
  if (sinMeta > 0) detalleCob.push(`${sinMeta} sin meta: ${motivosTexto.join(', ')}`);
  if (listos < total) detalleCob.push(`${total - listos} sin meta ni motivo`);
  // Quiénes no entran en el «de N» (la cobertura rehecha con `coberturaDeFilas`).
  const desglose = textoDesglosePuesto(r.cobertura.sinMetaPorPuesto ?? 0, r.cobertura.opcionales ?? 0);
  if (desglose) detalleCob.push(desglose);
  out.push({
    id: 'cobertura',
    tono: listos >= total ? 'ok' : 'alerta',
    titulo: corto
      ? `${listos} de ${total} con meta o motivo`
      : `${listos} de ${total} colaboradores con meta o motivo`,
    detalle: detalleCob,
  });

  // 2. Metas bajo la venta promedio.
  const bajo = r.bajoPromedio;
  const umbral = parametros ? ` (más de ${Math.round(parametros.umbralBajoPromedio * 100)} %)` : '';
  out.push({
    id: 'bajo-promedio',
    tono: bajo.length ? 'alerta' : 'ok',
    titulo: bajo.length
      ? corto
        ? `${bajo.length === 1 ? '1 meta' : `${bajo.length} metas`} debajo de su venta promedio`
        : `${bajo.length === 1 ? '1 meta' : `${bajo.length} metas`} por debajo de la venta promedio`
      : corto
        ? 'Ninguna meta debajo de su promedio'
        : `Ninguna meta por debajo de la venta promedio${umbral}`,
    detalle: bajo.map((b) =>
      corto
        ? `${nombreCorto(b.nombre)}: ${pesosEnteros(b.meta)} vs. ${pesosEnteros(b.promedio)} (${pctConSigno(b.pct)})`
        : `${nombrePropio(b.nombre)}: ${pesosEnteros(b.meta)} vs. ${pesosEnteros(b.promedio)} prom. (${pctConSigno(b.pct)})`,
    ),
  });

  // 2b. Metas mayores al doble del promedio (sólo si las hay: el diseño muestra 3 chequeos).
  if (muyAltas.length) {
    const veces = parametros?.factorMax ?? 2;
    out.push({
      id: 'muy-alta',
      tono: 'alerta',
      titulo: `${muyAltas.length === 1 ? '1 meta mayor' : `${muyAltas.length} metas mayores`} ${veces === 2 ? 'al doble' : `a ${veces} veces`} de su venta promedio`,
      detalle: muyAltas.map(
        (f) => `${corto ? nombreCorto(f.nombre) : nombrePropio(f.nombre)}: ${pesosEnteros(f.metaMonto)} vs. ${pesosEnteros(f.ventaPromedio)}`,
      ),
    });
  }

  // 3. Suma contra la meta de tienda.
  const s = r.suma;
  const incompleta = textoMetaIncompleta(metaTienda);
  if (!s && incompleta && metaTienda) {
    // Revisión de la fase B (21-oct-2026): el recuadro decía «Sólo 6 de 7 días
    // cargados» y aquí «Sin meta de tienda completa».
    out.push({
      id: 'suma',
      tono: 'info',
      titulo: corto ? `Meta de tienda incompleta (${metaTienda.dias} de 7 días)` : incompleta,
      detalle: corto ? [] : ['La suma no se compara'],
    });
  } else if (!s && metaTienda?.conIva) {
    out.push({
      id: 'suma',
      tono: 'info',
      titulo: corto ? 'Meta de tienda con IVA' : 'Meta de tienda con IVA: la suma no se compara',
      detalle: [],
    });
  } else if (!s) {
    out.push({
      id: 'suma',
      tono: 'info',
      titulo: corto ? 'Sin meta de tienda' : 'Sin meta de tienda completa: la suma no se compara',
      detalle: [],
    });
  } else {
    const pct = Math.abs(s.pct * 100).toLocaleString('es-MX', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
    const debajo = s.diferencia < 0;
    const tol = parametros ? ` (±${Math.round(parametros.toleranciaSuma * 100)} %)` : '';
    out.push({
      id: 'suma',
      tono: s.fuera ? 'alerta' : 'ok',
      titulo: s.fuera
        ? `Suma ${pct} % ${debajo ? 'debajo' : 'encima'} de la meta${corto ? '' : ' de tienda'}`
        : corto
          ? 'Suma dentro de la meta'
          : `Suma dentro de la meta de tienda${tol}`,
      detalle:
        s.diferencia === 0
          ? []
          : debajo
            ? [`Quedan ${pesosEnteros(-s.diferencia)} sin asignar`]
            : [`${pesosEnteros(s.diferencia)} por encima de la meta`],
    });
  }

  // 4. Bodegueros con meta (23-oct-2026, decisión del usuario), al final y sólo si
  // los hay. Es una advertencia: no cuenta en `alertas` ni saca a la tienda del lote.
  const bodegueros = r.bodeguerosConMeta ?? [];
  if (bodegueros.length) {
    const quien = (b: (typeof bodegueros)[number]) => (corto ? nombreCorto(b.nombre) : nombrePropio(b.nombre));
    const puesto = (b: (typeof bodegueros)[number]) => (b.puesto && b.puesto.trim() ? nombrePropio(b.puesto) : 'Bodeguero');
    out.push({
      id: 'bodeguero',
      tono: 'alerta',
      titulo:
        bodegueros.length === 1
          ? `${TEXTO_META_A_BODEGUERO}: ${quien(bodegueros[0])}`
          : `Se le está colocando meta a ${bodegueros.length} bodegueros`,
      detalle: [
        ...(bodegueros.length === 1
          ? [`${puesto(bodegueros[0])} · meta de ${pesosEnteros(bodegueros[0].meta)}`]
          : bodegueros.map((b) => `${quien(b)} (${puesto(b)}): ${pesosEnteros(b.meta)}`)),
        // Ronda F (23-oct-2026): decía «no impide autorizar, ni en lote» bajo una
        // «meta mayor al doble» de la misma persona que SÍ sacaba la tienda del
        // lote. Ya no sale esa alerta para el bodeguero (S1); su meta bajo el
        // promedio sí, y ésa es otra alerta, no este aviso.
        corto ? 'No impide autorizar' : 'Es opcional: este aviso no impide autorizar ni saca a la tienda del lote.',
      ],
    });
  }

  // 5. La Casa con meta (ronda F, 23-oct-2026, decisión del usuario): cuenta para la
  // meta de la tienda y nadie la cobra. Sólo informa, como el bodeguero.
  const casa = r.casaConMeta;
  if (casa && casa.meta > 0) {
    out.push({
      id: 'casa',
      tono: 'alerta',
      titulo: `${TEXTO_META_A_CASA}: ${casa.nombre} (${pesosEnteros(casa.meta)})`,
      detalle: corto
        ? ['Nadie la cobra · no impide autorizar']
        : ['Cuenta para la meta de la tienda; nadie la cobra, ni el gerente. No impide autorizar.'],
    });
  }
  return out;
}

/**
 * Lo que la lista de Autorización marca junto al nombre de la tienda (ronda F,
 * 23-oct-2026, decisión de la sesión, revertible): «Meta a bodeguero» y «Meta a
 * Casa», para que nadie las autorice en lote sin verlo. Sale de la revisión
 * GUARDADA de la fila (la API ya la manda en la lista); no saca del lote.
 */
export interface MetaExcepcional {
  tipo: 'bodeguero' | 'casa';
  etiqueta: string;
  /** «Se le está colocando meta al bodeguero: Juan Jesús Reyes Clemente ($91,970)». */
  detalle: string;
}

export function metasExcepcionales(r: RevisionAutomatica | null | undefined): MetaExcepcional[] {
  const out: MetaExcepcional[] = [];
  const bodegueros = (r?.bodeguerosConMeta ?? []).filter((b) => b && b.meta > 0);
  if (bodegueros.length)
    out.push({
      tipo: 'bodeguero',
      etiqueta: 'Meta a bodeguero',
      detalle: `${TEXTO_META_A_BODEGUERO}: ${bodegueros.map((b) => `${nombrePropio(b.nombre) || b.nombre} (${pesosEnteros(b.meta)})`).join(', ')}`,
    });
  const casa = r?.casaConMeta;
  if (casa && casa.meta > 0)
    out.push({ tipo: 'casa', etiqueta: 'Meta a Casa', detalle: `${TEXTO_META_A_CASA}: ${casa.nombre} (${pesosEnteros(casa.meta)})` });
  return out;
}

/** Las llaves de las filas con alerta (bajo el promedio o muy altas): van en ámbar. */
export function llavesConAlerta(r: RevisionAutomatica | null | undefined, muyAltas: FilaVista[] = []): Set<string> {
  return new Set([...(r?.bajoPromedio ?? []).map((b) => b.key), ...muyAltas.map((f) => f.key)]);
}

// ------------------------------------------------------------ historial

const VERBO: Record<string, { el: string; tu: string }> = {
  // 21-oct-2026 (fase B): el gerente desde la app y la devolución del supervisor.
  enviar_supervisor: { el: 'envió al supervisor', tu: 'Enviaste al supervisor' },
  devolver: { el: 'devolvió al gerente', tu: 'Devolviste al gerente' },
  enviar: { el: 'envió a autorización', tu: 'Enviaste a autorización' },
  autorizar: { el: 'autorizó', tu: 'Autorizaste' },
  rechazar: { el: 'rechazó', tu: 'Rechazaste' },
  reabrir: { el: 'reabrió', tu: 'Reabriste' },
  guardar: { el: 'guardó la captura', tu: 'Guardaste la captura' },
  'meta.liberada': { el: 'liberó una meta (la reclamó su tienda de plantilla)', tu: 'Liberaste una meta' },
};

/**
 * Un renglón del historial sin la fecha: «Karla Ríos envió a autorización»,
 * «Arturo Barrios rechazó: “Ajustar…”». Con `segundaPersona` y un evento de la
 * cuenta que mira (`esMio`): «Rechazaste: “Ajustar…”».
 */
export function textoEvento(e: EventoHistorial, segundaPersona = false): string {
  const v = VERBO[e.accion];
  const comentario = e.comentario ? `: “${e.comentario}”` : '';
  const envio = e.accion === 'enviar' && (e.numEnvio ?? 0) > 1 ? ` (${ordinalEnvio(e.numEnvio as number)})` : '';
  // El gerente envía con la cuenta de la TIENDA: se nombra a la persona que eligió
  // «Cambiar usuario» (informativo, 21-oct-2026) y se dice que es el gerente.
  const quien =
    e.accion === 'enviar_supervisor' && e.persona
      ? `${nombrePropioSiMayusculas(e.persona)} (gerente)`
      : nombrePropioSiMayusculas(e.nombre);
  if (!v) return `${quien} · ${e.accion}${comentario}`;
  if (segundaPersona && e.esMio) return `${v.tu}${envio}${comentario}`;
  return `${quien} ${v.el}${envio}${comentario}`;
}

/** Los nombres de `users.name` se respetan; sólo los que vienen en MAYÚSCULAS se pasan a nombre propio. */
export function nombrePropioSiMayusculas(n: string): string {
  return n && n === n.toLocaleUpperCase('es-MX') && /[A-ZÁÉÍÓÚÑ]/.test(n) ? nombrePropio(n) : n;
}

/**
 * El único renglón del historial en el teléfono: el más reciente CON
 * comentario (el rechazo que explica el reenvío), y si no hay, el más reciente.
 */
export function eventoDestacado(h: EventoHistorial[]): EventoHistorial | null {
  return h.find((e) => !!e.comentario) ?? h[0] ?? null;
}

// ------------------------------------------------------------ lote

/** El resumen del lote en una frase, para el aviso. */
export function resumenLote(r: { autorizadas: unknown[]; omitidas: unknown[]; fallidas: unknown[] }): string {
  const partes = [`${r.autorizadas.length} autorizada(s)`];
  if (r.omitidas.length) partes.push(`${r.omitidas.length} omitida(s) por alertas`);
  if (r.fallidas.length) partes.push(`${r.fallidas.length} no se pudieron autorizar`);
  return partes.join(' · ');
}
